// Curated catalog of all platform engines, grouped into 9 capability groups
// + 4 flagship product engines. Used by EnginesPage and the landing page's
// foundationEngines summary. Each entry includes status (LIVE/BETA/PLANNED),
// source path (transparency), and one-line description.

export const FLAGSHIP_ENGINES = [
  {
    slug: 'funding',
    name: 'Funding Engine',
    color: '#3EF1C7',
    icon: 'CreditCard',
    description: '13-week cashflow projection + collateral assessment + multi-lender routing.',
    status: 'live',
    subEngines: [
      'Cashflow Engine', 'Liquidity Engine', 'Risk Engine', 'Collateral Engine',
      'Transparency Engine', 'Banking Partners Engine', 'MCA Protection', 'Extended Forecast',
    ],
    source: 'backend/funding/',
  },
  {
    slug: 'distribution',
    name: 'Distribution Engine',
    color: '#7A5CFF',
    icon: 'Globe',
    description: 'Demand sensing → content generation → multi-channel distribution → virality loops.',
    status: 'live',
    subEngines: ['Demand Engine', 'Content Engine', 'Growth Engine', 'Virality Engine'],
    source: 'backend/distribution/',
  },
  {
    slug: 'feedback',
    name: 'Feedback Engine',
    color: '#FFD166',
    icon: 'MessageSquare',
    description: 'Agentic NPS, sentiment analysis, voice-of-customer reports, auto-action triggers.',
    status: 'live',
    subEngines: ['NPS tracker', 'Sentiment analyzer', 'Voice-of-customer report builder', 'Auto-action triggers'],
    source: 'backend/feedback_engine/',
  },
  {
    slug: 'product',
    name: 'Product Engine',
    color: '#F55454',
    icon: 'Rocket',
    description: 'Product discovery agent, launch orchestration, revenue-stream analysis, market-fit scoring.',
    status: 'live',
    subEngines: ['Discovery agent', 'Launch orchestration', 'Revenue-stream analysis', 'Market-fit scorer'],
    source: 'backend/product_engine/',
  },
];

export const FOUNDATION_GROUPS = [
  {
    slug: 'health-scoring',
    name: 'Health & Scoring',
    color: '#3EF1C7',
    icon: 'Activity',
    description: 'Unified merchant score from 3 dimensions: CI Score, Margin Guard, Decision Graph.',
    engines: [
      { name: 'Commerce Health Score', status: 'live', desc: 'Unified 0-1000 merchant score (40% CI + 30% Margin Guard + 30% Decision Graph)', source: 'backend/commerce_health_score.py' },
      { name: 'CI Score v2', status: 'live', desc: 'Enhanced deal scoring across 3 dimensions × 14 factors', source: 'backend/scoring_engine_v2.py' },
      { name: 'Guardrail Engine', status: 'live', desc: 'Hard/soft/adaptive safety limits on every agent action', source: 'backend/guardrail_engine.py' },
    ],
  },
  {
    slug: 'memory-audit',
    name: 'Memory & Audit',
    color: '#7A5CFF',
    icon: 'Brain',
    description: '5-tier memory stack (Mem0 + Graphiti + Cognee + gbrain + Letta) + tamper-evident conversation transcripts.',
    engines: [
      { name: 'Memory Facade', status: 'live', desc: 'Unified interface across 5 memory tiers with provenance', source: 'backend/memory/facade.py' },
      { name: 'Entity Resolver', status: 'live', desc: 'Canonical entity identity with fuzzy dedup', source: 'backend/memory/entity_resolver.py' },
      { name: 'Transcript Writer', status: 'live', desc: 'Async audit trail with hash chains + daily Merkle roots', source: 'backend/audit/transcript_writer.py' },
    ],
  },
  {
    slug: 'intelligence',
    name: 'Intelligence & Discovery',
    color: '#FFD166',
    icon: 'Sparkles',
    description: 'What to predict, what to recommend, what to avoid. Powered by Margin Guard + Prediction + Opportunity.',
    engines: [
      { name: 'Prediction Engine', status: 'live', desc: 'Demand forecast, churn risk, reorder timing, stockout risk', source: 'backend/intelligence/prediction_engine.py' },
      { name: 'Opportunity Engine', status: 'live', desc: 'Cross-sell, upsell, restock, win-back, channel expansion', source: 'backend/intelligence/opportunity_engine.py' },
      { name: 'Margin Guard', status: 'live', desc: 'Live margin protection across SKUs and channels', source: 'backend/margin_guard_service.py' },
      { name: 'Context Graph V2', status: 'live', desc: 'Decision precedents + what-if simulator + effective confidence', source: 'backend/decision_graph/context_graph_v2.py' },
    ],
  },
  {
    slug: 'orchestration',
    name: 'Orchestration',
    color: '#F55454',
    icon: 'Network',
    description: 'How agents coordinate. Playbooks, flows, conversational routing, and the agent work engine.',
    engines: [
      { name: 'Playbook Engine', status: 'live', desc: 'Universal playbook discovery, parsing, inheritance, runtime application', source: 'backend/commerce_aging/playbook_engine.py' },
      { name: 'Flow Engine', status: 'live', desc: 'State machine for P2P, O2C, P2F, seasonal, drop, RA flows', source: 'backend/commerce_aging/flows/flow_engine.py' },
      { name: 'Conversational Router', status: 'live', desc: 'WhatsApp/iMessage/SMS → Claude tools → response', source: 'backend/messaging/conversational_router.py' },
      { name: 'Agent Work Engine', status: 'live', desc: 'Task orchestration for external agents (research, fetch deals, audit)', source: 'backend/agents/agent_work_engine.py' },
    ],
  },
  {
    slug: 'inventory-fulfillment',
    name: 'Inventory & Fulfillment',
    color: '#3EF1C7',
    icon: 'Package',
    description: 'ATP, allocation, packaging, routing across nodes. The fulfillment brain.',
    engines: [
      { name: 'Allocation Engine', status: 'live', desc: 'Omnichannel inventory reservation (SFS, BOPIS, ROPIS, drop-ship, cross-dock)', source: 'backend/inventory/allocation_engine.py' },
      { name: 'Sourcing Engine', status: 'live', desc: 'Decides where inventory comes from: warehouse, store, vendor-direct, 3PL', source: 'backend/inventory/sourcing_engine.py' },
      { name: 'Cubing Engine', status: 'live', desc: '3D bin packing optimization (FFD + skyline, 92-96% utilization)', source: 'backend/supply_chain/cubing_engine.py' },
      { name: 'Aging Pipeline', status: 'live', desc: 'Time-sensitive order staging (remorse, fraud check, sourcing, ship, auth)', source: 'backend/orders/aging_pipeline.py' },
      { name: 'Order Orchestration Engine', status: 'live', desc: 'Enterprise order lifecycle: decomposition, allocation, shipping order creation', source: 'backend/orders/orchestration_engine.py' },
    ],
  },
  {
    slug: 'decision-graph',
    name: 'Decision Graph',
    color: '#7A5CFF',
    icon: 'Layers',
    description: 'Every decision: precedents, what-ifs, policy gates, settlement.',
    engines: [
      { name: 'Context Graph V2', status: 'live', desc: 'Outcomes + precedent effective confidence with what-if simulator', source: 'backend/decision_graph/context_graph_v2.py' },
      { name: 'Policy Gate', status: 'live', desc: 'Decision approval routing based on policy thresholds', source: 'backend/decision_graph/policy_gate.py' },
      { name: 'Settlement Engine', status: 'live', desc: 'Payment capture, platform fee collection, merchant payouts', source: 'backend/finance/settlement_engine.py' },
    ],
  },
  {
    slug: 'marketplace-coordination',
    name: 'Marketplace Coordination',
    color: '#FFD166',
    icon: 'Users',
    description: 'Two-sided coordination: 3PLs bid for sellers, agents earn reputation, ideas get T-shirt sized.',
    engines: [
      { name: 'RFQ Matching', status: 'live', desc: '3PL provider matching by capability, region, capacity', source: 'backend/three_pl_portal/rfq_matching.py' },
      { name: 'Reputation Scorer', status: 'live', desc: '7-dimension agent + 3PL reputation with Merkle proofs', source: 'backend/reputation/reputation_scorer_agent.py' },
      { name: 'T-shirt Cost Estimator', status: 'live', desc: 'Idea → XS/S/M/L/XL cost estimate across 8 business categories', source: 'backend/portfolio/cost_estimator.py' },
    ],
  },
  {
    slug: 'creator-analytics',
    name: 'Creator Analytics',
    color: '#F55454',
    icon: 'LineChart',
    description: 'Cohort retention, LTV, content→revenue attribution — the metrics Substack does not show.',
    engines: [
      { name: 'Cohort Retention', status: 'live', desc: 'Monthly cohort retention matrix across all connected creator platforms', source: 'backend/creator/analytics/cohort.py' },
      { name: 'LTV Calculator', status: 'live', desc: 'Lifetime value by platform, cohort, acquisition source', source: 'backend/creator/analytics/ltv.py' },
      { name: 'Attribution Engine', status: 'live', desc: 'Content piece → revenue attribution (first/last/linear)', source: 'backend/creator/analytics/attribution.py' },
      { name: 'Insight Generator', status: 'live', desc: '240-480 char WhatsApp/SMS analytics summaries', source: 'backend/creator/analytics/insight_generator.py' },
    ],
  },
  {
    slug: 'financial-health',
    name: 'Financial Health',
    color: '#3EF1C7',
    icon: 'DollarSign',
    description: 'Customer/product profitability, MCA crisis prevention, extended cashflow forecasting.',
    engines: [
      { name: 'Customer Profitability', status: 'live', desc: 'Per-customer P&L with segmentation and retention impact', source: 'backend/financial_health/customer_profitability.py' },
      { name: 'Product Profitability', status: 'live', desc: 'Per-product P&L with channel + carrier + return cost layered in', source: 'backend/financial_health/product_profitability.py' },
      { name: 'MCA Protection', status: 'live', desc: 'Predicts and prevents merchant cash advance crisis', source: 'backend/financial_health/mca_protection.py' },
      { name: 'Extended Forecast', status: 'live', desc: 'Long-horizon cashflow + revenue + spend forecasting', source: 'backend/financial_health/extended_forecast.py' },
    ],
  },
];

export const TOTAL_ENGINES = (
  FLAGSHIP_ENGINES.reduce((s, e) => s + (e.subEngines?.length || 1), 0) +
  FOUNDATION_GROUPS.reduce((s, g) => s + g.engines.length, 0)
);

// Status badge colors
export const STATUS_COLOR = {
  live: '#10B981',
  beta: '#3EF1C7',
  in_progress: '#FFD166',
  planned: '#94A3B8',
  coming_soon: '#7A5CFF',
};

export const STATUS_LABEL = {
  live: 'LIVE',
  beta: 'BETA',
  in_progress: 'IN PROGRESS',
  planned: 'PLANNED',
  coming_soon: 'COMING SOON',
};
